import React, { useState } from 'react';
import { Button, IconButton, Tooltip } from '@mui/material';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Field, LinkFieldValue } from '@sitecore-jss/sitecore-jss-react';
import CustomTooltip from '../../CustomTooltip/CustomTooltip';
import { Oval } from 'react-loader-spinner';

interface EnrollmentKeysSectionProps {
  orderId: string;
  productId?: string | null;
  enrollmentKey?: string | null;
  quantity?: number;
  trainingRedirectionLink?: Field<LinkFieldValue>;
  learnUponRedirectionLink: Field<LinkFieldValue>;
  TooltipText?: Field<string>;
  isLoading?: boolean;
}

export const EnrollmentKeysSection = ({
  productId,
  orderId,
  trainingRedirectionLink,
  learnUponRedirectionLink,
  enrollmentKey,
  quantity,
  TooltipText,
  isLoading,
}: EnrollmentKeysSectionProps): JSX.Element => {
  const [copied, setCopied] = useState(false);
  const [showKey, setShowKey] = useState(false);

  if (!enrollmentKey && quantity === 1 && learnUponRedirectionLink?.value?.href) {
    const redirectUrl = learnUponRedirectionLink.value.href.replace(
      '##ProductId##',
      productId ?? ''
    );
    return (
      <a
        href={isLoading ? undefined : redirectUrl}
        target={learnUponRedirectionLink.value.target}
        rel="noopener noreferrer"
        className="redirectionButton"
      >
        <Button
          color="secondary"
          variant="contained"
          className={`buttonFillBlue ${isLoading ? 'enrolling-border' : ''}`}
        >
          {isLoading ? 'ENROLLING IN COURSE...' : learnUponRedirectionLink?.value.text}
        </Button>
      </a>
    );
  }

  if (quantity === 1) return <></>;

  const handleCopy = () => {
    if (!showKey || !enrollmentKey) return;
    navigator.clipboard.writeText(enrollmentKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const toggleShowKey = () => {
    setShowKey((prev) => !prev);
  };

  const displayKey = enrollmentKey
    ? showKey
      ? enrollmentKey
      : enrollmentKey.slice(0, 4) + '*'.repeat(enrollmentKey.length - 4)
    : '';

  return (
    <>
      <div className="enrollmentKeysWrapper">
        <div className="heading">
          Enrollment Key:{' '}
          <span>{TooltipText && <CustomTooltip fields={{ 'Tooltip Text': TooltipText }} />}</span>
        </div>

        <div className="enrollmentSection">
          <span className="enrollmentLoader">
            {isLoading ? (
              <>
                <span className="loaderText">Generating key </span>
                <span className="loadingSpinner">
                  <Oval
                    visible
                    height={18}
                    width={18}
                    color="#0051a2"
                    strokeWidth={4}
                    secondaryColor="#0051a2"
                  />
                </span>
              </>
            ) : (
              displayKey
            )}
          </span>

          <div className="enrollmentIcons">
            {showKey && enrollmentKey && (
              <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
                <IconButton onClick={handleCopy} size="small">
                  <FileCopyIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            )}
            {enrollmentKey && (
              <Tooltip title={showKey ? 'Hide Key' : 'Show Key'}>
                <IconButton onClick={toggleShowKey} size="small">
                  {showKey ? (
                    <VisibilityOffIcon sx={{ fontSize: 20 }} />
                  ) : (
                    <VisibilityIcon sx={{ fontSize: 20 }} />
                  )}
                </IconButton>
              </Tooltip>
            )}
          </div>
        </div>
      </div>

      {trainingRedirectionLink?.value?.href && !isLoading && (
        <a
          href={trainingRedirectionLink.value.href.replace('##OrderID##', orderId)}
          target={trainingRedirectionLink.value.target}
          rel="noopener noreferrer"
          className="redirectionButton"
        >
          <Button color="secondary" variant="contained" className="buttonFillBlue">
            {trainingRedirectionLink.value.text}
          </Button>
        </a>
      )}
    </>
  );
};

export default EnrollmentKeysSection;
